import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import { Roles } from '@/auth/azure/Roles'
import DateColumn from '@/components/date-column/DateColumn'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { attivita_lavorativa } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { HourEditorFormActivitiesGridProps } from '@/routes/hours/HourEditor.types'
import { calculateTempo, computeTempo } from '@/routes/hours/utils'
import { isUserRoleAllowed, onDataGridSelectedRowButtonClick } from '@/routes/utils'
import { useScreenSize } from '@/themes/media-query'
import { AxiosError, AxiosResponse } from 'axios'
import { Button } from 'devextreme-react'
import {
  Column,
  DataGrid,
  DataGridRef,
  DataGridTypes,
  Editing,
  Item,
  Paging,
  Summary,
  Toolbar,
  TotalItem,
  Button as GridButton,
  StateStoring,
} from 'devextreme-react/data-grid'
import DataSource from 'devextreme/data/data_source'
import ODataStore from 'devextreme/data/odata/store'
import { ClickEvent } from 'devextreme/ui/button'
import type { RowDblClickEvent } from 'devextreme/ui/data_grid'
import notify from 'devextreme/ui/notify'
import { memo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const HourEditorFormActivitiesGrid = (props: HourEditorFormActivitiesGridProps) => {
  const { impiegato, data, readOnly, userInfo } = props
  const currentScreenSize = useScreenSize()
  const { client } = useQsAdminApiManager()

  const { hourId } = useParams()
  const token = useTokenRefresh()
  const navigate = useNavigate()
  const gridRef = useRef<DataGridRef>(null)

  const computeTempoUfficioValue = (rowData: attivita_lavorativa) => {
    return computeTempo(rowData.tempo_ufficio)
  }

  const computeTempoClienteValue = (rowData: attivita_lavorativa) => {
    return computeTempo(rowData.tempo_cliente)
  }

  const computeTempoTrasfertaValue = (rowData: attivita_lavorativa) => {
    return computeTempo(rowData.tempo_trasferta)
  }

  const hourDate: Date = new Date(data ?? '')
  hourDate.setHours(0)
  hourDate.setMinutes(0)
  hourDate.setSeconds(0)
  hourDate.setMilliseconds(0)
  console.log('hourDate', hourDate)
  const startDateFilter: Date = new Date(hourDate.toISOString().replace('Z', ''))
  const endDateFilter: Date = new Date(
    new Date(hourDate.getFullYear(), hourDate.getMonth(), hourDate.getDate() + 1, 0, 0, 0)
      .toISOString()
      .replace('Z', ''),
  )

  const dailyActivitiesDataSource = new DataSource({
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/attivita_lavorativa`,
      key: 'id',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    filter: [
      ['impiegato.id', impiegato?.id],
      ['data', '>=', startDateFilter],
      ['data', '<', endDateFilter],
    ],
    expand: ['centro_costo', 'causale', 'piattaforma', 'sede($expand=azienda)', 'task', 'intervento'],
  })

  const handleDuplicateActivityClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: attivita_lavorativa) => {
      gridRef?.current?.instance().beginCustomLoading('Duplicazione attività in corso....')
      client
        .post(
          '/api/attivitalavorativa/duplicateactivity',
          { activityId: rowdata.id, activityDate: gridRef.current?.instance().getVisibleRows()[0].data.data },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then((response: AxiosResponse) => {
          gridRef.current?.instance().refresh()
        })
        .catch((error: AxiosError) => {
          notify(`Errore duplicazione attività. Dettagli : ${error}`, 'error', 2000)
        })
        .finally(() => {
          gridRef.current?.instance().endCustomLoading()
        })
    })
  }

  return (
    <>
      <DataGrid
        id={`daily-activity-grid`}
        dataSource={dailyActivitiesDataSource}
        noDataText="Nessuna attività inserita nella data indicata"
        ref={gridRef}
        keyExpr={'id'}
        showBorders={true}
        showColumnLines={true}
        focusedRowEnabled={true}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        allowColumnReordering={true}
        allowColumnResizing={true}
        rowAlternationEnabled={true}
        wordWrapEnabled={false}
        repaintChangesOnly={true}
        width="100%"
        onRowDblClick={(e: RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') navigate(`/workactivities/hours/${e.data.id}`)
        }}
        onRowRemoving={(e: DataGridTypes.RowRemovingEvent) => {
          console.log('e removing', e)
          const rowHour: attivita_lavorativa = e.data
          if (rowHour.id === Number(hourId)) {
            console.log('qui')
            notify(
              `Impossibile eliminare l'attività lavorativa ${rowHour.ded_Dis}. Attività in editazione`,
              'warning',
              3000,
            )
            e.cancel = true
          }
        }}
      >
        <StateStoring enabled={true} type="localStorage" storageKey={'hours-daily-datagrid'} savingTimeout={50} />
        <Paging defaultPageSize={8} />
        <Editing mode="batch" allowAdding={false} allowDeleting={!readOnly} allowUpdating={false} />
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item location="after">
            <Button
              visible={!readOnly}
              icon="add"
              hint="Nuova attività"
              onClick={(e: ClickEvent) => {
                if (
                  gridRef.current?.instance().getVisibleRows() &&
                  gridRef.current.instance().getVisibleRows().length > 0
                )
                  navigate({
                    pathname: '/workactivities/hours/new',
                    search: `?date=${gridRef.current.instance().getVisibleRows()[0].data.data}`,
                  })
                else notify(`Deve essere presente almeno un' attività per il giorno indicato.`, 'warning', 3000)
              }}
            />
          </Item>
          <Item
            visible={!readOnly}
            widget="dxButton"
            options={{
              hint: 'Duplica',
              icon: 'copy',
              onClick: handleDuplicateActivityClick,
              stylingMode: 'text',
            }}
          ></Item>
          <Item name="saveButton" />
          <Item name="revertButton" />
        </Toolbar>
        {DateColumn({
          dataField: 'data',
          caption: 'DATA',
          format: 'dd/MM/yyyy',
          defaultSortOrder: 'desc',
          // width: '6%',
          hidingPriority:
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined,
        })}
        <Column
          dataField={'centro_costo.nome'}
          caption={'CENTRO DI COSTO'}
          // width={'8%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 15 : undefined
          }
        />
        ,
        <Column
          dataField={'causale.nome'}
          caption="CAUSALE"
          // width={'3%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
          }
        />
        <Column
          dataField={'piattaforma.nome'}
          caption={'PIATTAFORMA'}
          // width={'7%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 14 : undefined
          }
        />
        <Column
          dataField={'task.ded_Dis'}
          caption={'TASK'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: attivita_lavorativa = cellData.row.data
            return (
              <ObjectCodeLink
                url={`/tasks/${data.task?.id}`}
                text={data.task?.ded_Dis}
                disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              ></ObjectCodeLink>
            )
          }}
          // width={'6%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 13 : undefined
          }
        />
        <Column
          dataField={'intervento.ded_Dis'}
          caption={'INTERVENTO'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: attivita_lavorativa = cellData.row.data
            return (
              <ObjectCodeLink
                url={`/interventions/${data.intervento?.id}`}
                text={data.intervento?.ded_Dis}
                disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              ></ObjectCodeLink>
            )
          }}
          // width={'6%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined
          }
        />
        <Column
          dataField={'sede.azienda.nome'}
          caption="AZIENDA"
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: attivita_lavorativa = cellData.row.data
            return (
              <ObjectCodeLink
                url={`/clients/${data.sede?.azienda?.id}`}
                text={data.sede?.azienda?.nome}
                disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              ></ObjectCodeLink>
            )
          }}
          // width={'8%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 11 : undefined
          }
        />
        <Column
          dataField={'sede.nome'}
          caption="SEDE"
          // width={'8%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
          }
        />
        <Column
          name={'tempo_ufficio'}
          caption="UFFICIO"
          // width={'4%'}
          calculateCellValue={computeTempoUfficioValue}
          dataType="datetime"
          format={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 10 : undefined
          }
        />
        <Column
          name={'tempo_cliente'}
          caption="CLIENTE"
          // width={'5%'}
          calculateCellValue={computeTempoClienteValue}
          dataType="datetime"
          format={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 9 : undefined
          }
        />
        <Column
          name={'tempo_trasferta'}
          caption="TRASFERTA"
          // width={'6%'}
          calculateCellValue={computeTempoTrasfertaValue}
          dataType="datetime"
          format={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
          }
        />
        <Column
          dataField={'spese_trasferta'}
          caption="SPESE TRASFERTA"
          // width={'4%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
        />
        <Column
          dataField={'spese_vitto'}
          caption="VITTO"
          // width={'4%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
          }
        />
        <Column
          dataField={'spese_alloggio'}
          caption="ALLOGGIO"
          // width={'4%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
          }
        />
        <Column
          dataField={'note'}
          caption="NOTE"
          width={'30%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
          }
        />
        <Column
          type="buttons"
          width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
          alignment="left"
        >
          <GridButton name="delete"></GridButton>
        </Column>
        <Summary calculateCustomSummary={calculateTempo}>
          <TotalItem
            name="TempoClienteUfficioTotale"
            summaryType="custom"
            customizeText={(itemInfo: any) => {
              return `HH.MM ${itemInfo.value.ore}.${itemInfo.value.minuti}`
            }}
            showInColumn="tempo_cliente"
          />
          <TotalItem
            name="TempoTrasfertaTotale"
            summaryType="custom"
            displayFormat="HH {0}"
            showInColumn="tempo_trasferta"
          />
          <TotalItem column="spese_trasferta" summaryType="sum" displayFormat="€ {0}" />
          <TotalItem column="spese_vitto" summaryType="sum" displayFormat="€ {0}" />
          <TotalItem column="spese_alloggio" summaryType="sum" displayFormat="€ {0}" />
        </Summary>
      </DataGrid>
    </>
  )
}

const HourEditorFormActivitiesGridMemoed = memo(HourEditorFormActivitiesGrid, (oldProps, newProps) => {
  return oldProps.impiegato?.id === newProps.impiegato?.id && oldProps.data === newProps.data
})

export default HourEditorFormActivitiesGridMemoed
